import React, { useState } from 'react';
import {
    Table,
    Card,
    Typography,
    Space,
    Input,
    Select,
    Button,
    Tag,
    Popconfirm,
    message
} from 'antd';
import {
    SearchOutlined,
    SyncOutlined,
    InfoCircleOutlined,
    ChromeOutlined,
    LockOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    StopOutlined,
    UnlockOutlined
} from '@ant-design/icons';
import { useQuery, useMutation } from '@tanstack/react-query';
import apiClient from '../../api/axios';

const { Title, Text } = Typography;

interface LoginAttemptLogDto {
    id: string;
    email: string;
    ipAddress: string;
    isSuccess: boolean;
    failureReason?: string;
    userAgent?: string;
    createdAt: string;
    isBlacklisted: boolean;
}

interface PagedResult<T> {
    succeeded: boolean;
    data: T[];
    pageNumber: number;
    pageSize: number;
    totalRecords: number;
    totalPages: number;
    errors?: string[];
}

export const LoginLogsList: React.FC = () => {
    const [pageIndex, setPageIndex] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [isSuccess, setIsSuccess] = useState<boolean | undefined>(undefined);
    const [search, setSearch] = useState<string>('');

    const fetchLoginLogs = async () => {
        const params = new URLSearchParams({
            page: pageIndex.toString(),
            pageSize: pageSize.toString(),
            ...(search && { search }),
            ...(isSuccess !== undefined && { isSuccess: isSuccess.toString() })
        });
        const response = await apiClient.get<PagedResult<LoginAttemptLogDto>>(`/admin/auth/login-logs?${params}`);
        return response.data;
    };

    const { data: pagedResult, isLoading, refetch } = useQuery<PagedResult<LoginAttemptLogDto>>({
        queryKey: ['loginLogs', pageIndex, pageSize, search, isSuccess],
        queryFn: fetchLoginLogs
    });

    const blacklistMutation = useMutation({
        mutationFn: async (ip: string) => {
            await apiClient.post('/admin/auth/blacklist', { ipAddress: ip, reason: 'Admin blacklist' });
        },
        onSuccess: () => {
            message.success('IP adresi başarıyla engellendi.');
            refetch();
        },
        onError: () => {
            message.error('IP engelleme başarısız oldu.');
        }
    });

    const unblacklistMutation = useMutation({
        mutationFn: async (ip: string) => {
            await apiClient.delete(`/admin/auth/blacklist/${ip}`);
        },
        onSuccess: () => {
            message.success('IP adresinin engeli başarıyla kaldırıldı.');
            refetch();
        },
        onError: () => {
            message.error('Engeli kaldırma başarısız oldu.');
        }
    });

    const columns = [
        {
            title: 'Tarih',
            dataIndex: 'createdAt',
            key: 'createdAt',
            width: 180,
            render: (text: string) => new Date(text).toLocaleString()
        },
        {
            title: 'E-posta',
            dataIndex: 'email',
            key: 'email',
            render: (email: string) => <Text strong>{email}</Text>
        },
        {
            title: 'IP Adresi',
            dataIndex: 'ipAddress',
            key: 'ipAddress',
            width: 220,
            render: (ip: string, record: LoginAttemptLogDto) => (
                <Space>
                    <Tag color={record.isBlacklisted ? 'red' : 'blue'}>{ip}</Tag>
                    {record.isBlacklisted && <Tag color="error" style={{ fontWeight: 'bold' }}>ENGELLENDİ</Tag>}
                </Space>
            )
        },
        {
            title: 'Durum',
            dataIndex: 'isSuccess',
            key: 'isSuccess',
            width: 130,
            render: (success: boolean) => (
                success 
                    ? <Tag color="success" icon={<CheckCircleOutlined />}>Başarılı</Tag> 
                    : <Tag color="error" icon={<CloseCircleOutlined />}>Başarısız</Tag>
            )
        },
        {
            title: 'Hata Detayı / Sonuç',
            dataIndex: 'failureReason',
            key: 'failureReason',
            render: (reason?: string, record?: LoginAttemptLogDto) => {
                if (record?.isSuccess) {
                    return <Text type="secondary">Giriş Başarılı</Text>;
                }
                return <Text type="danger" style={{ fontWeight: 500 }}>{reason || 'Bilinmeyen Hata'}</Text>;
            }
        },
        {
            title: 'İşlem',
            key: 'action',
            width: 150,
            render: (_: any, record: LoginAttemptLogDto) => (
                record.isBlacklisted ? (
                    <Popconfirm
                        title={`${record.ipAddress} engeli kaldırılsın mı?`}
                        onConfirm={() => unblacklistMutation.mutate(record.ipAddress)}
                        okText="Evet"
                        cancelText="Hayır"
                    >
                        <Button type="primary" size="small" icon={<UnlockOutlined />}>
                            Engeli Kaldır
                        </Button>
                    </Popconfirm>
                ) : (
                    <Popconfirm
                        title={`${record.ipAddress} engellensin mi?`}
                        onConfirm={() => blacklistMutation.mutate(record.ipAddress)}
                        okText="Evet"
                        cancelText="Hayır"
                        okButtonProps={{ danger: true }}
                    >
                        <Button type="primary" danger size="small" icon={<StopOutlined />}>
                            Engelle
                        </Button>
                    </Popconfirm>
                )
            )
        }
    ];

    const expandedRowRender = (record: LoginAttemptLogDto) => {
        return (
            <div style={{ background: '#f8f9fa', padding: '16px 24px', borderRadius: 8, border: '1px solid #e8e8e8' }}>
                <div style={{ marginBottom: 12 }}>
                    <Text strong style={{ display: 'block', marginBottom: 4 }}>
                        <ChromeOutlined /> Tarayıcı / Cihaz Bilgisi (User Agent):
                    </Text>
                    <pre style={{ background: '#fff', padding: 12, borderRadius: 4, border: '1px solid #d9d9d9', overflowX: 'auto', fontSize: '12px', margin: 0 }}>
                        {record.userAgent || 'Belirtilmemiş'}
                    </pre>
                </div>
                {!record.isSuccess && record.failureReason && (
                    <div>
                        <Text strong style={{ display: 'block', marginBottom: 4 }}>
                            <InfoCircleOutlined style={{ color: '#ff4d4f' }} /> Loglanan Hata Mesajı:
                        </Text>
                        <div style={{ background: '#fff1f0', color: '#a8071a', padding: 12, borderRadius: 4, border: '1px solid #ffa39e', fontSize: '13px' }}>
                            {record.failureReason}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <Card
            title={
                <Space>
                    <LockOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                    <Title level={4} style={{ margin: 0 }}>Yönetici Giriş Denemeleri Günlüğü</Title>
                </Space>
            }
            extra={
                <Button type="primary" icon={<SyncOutlined />} onClick={() => refetch()} loading={isLoading}>
                    Yenile
                </Button>
            }
        >
            <div style={{ display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap' }}>
                <Input
                    placeholder="E-posta veya IP adresi ara..."
                    allowClear
                    prefix={<SearchOutlined />}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ width: 300 }}
                />
                <Select
                    allowClear
                    placeholder="Giriş Durumu Filtresi"
                    style={{ width: 200 }}
                    value={isSuccess}
                    onChange={(val) => setIsSuccess(val)}
                    options={[
                        { value: true, label: 'Sadece Başarılı Girişler' },
                        { value: false, label: 'Sadece Başarısız Girişler' },
                    ]}
                />
            </div>

            <Table
                columns={columns}
                dataSource={pagedResult?.data || []}
                rowKey="id"
                loading={isLoading || blacklistMutation.isPending || unblacklistMutation.isPending}
                expandable={{ 
                    expandedRowRender,
                    rowExpandable: () => true 
                }}
                pagination={{
                    current: pageIndex,
                    pageSize: pageSize,
                    total: pagedResult?.totalRecords || 0,
                    showSizeChanger: true,
                    onChange: (page, size) => {
                        setPageIndex(page);
                        setPageSize(size);
                    }
                }}
            />
        </Card>
    );
};
